<template>
    <div class="grid">
        <div class="unit whole">
            <h1>View Pupils</h1>
            <p class="lead">
                To move pupils as a class when the school year changes, go to 'Classes' and change the name of their
                class.
                <a href="/downloads/Changing-Classes-Guidelines.pdf" download="">See guidelines</a>
            </p>
            <v-alert v-if="unallocatedPupils.length > 0" type="info" class="mb-3">
                <div>
                    {{ unallocatedPupils[0].name }}
                    {{
                        unallocatedPupils.length > 1
                            ? 'and ' + (unallocatedPupils.length - 1) + ' other pupils are'
                            : 'is'
                    }}
                    not allocated to a class. Allocate pupils to a class to assign them texts and tasks.
                </div>
                <div class="d-flex justify-end mt-3">
                    <v-btn variant="outlined" to="/admin/alloc-pupils?class=unallocated">View unallocated pupils</v-btn>
                </div>
            </v-alert>
            <v-btn color="brand" :disabled="selectedRows.length === 0" class="pull-right" @click="deletePupilRows"
                >Delete selected</v-btn
            >
            <v-btn color="brand" to="/admin/alloc-pupils" class="pull-right mx-5">Allocate pupils</v-btn>
            <v-btn color="brand" to="/admin/add-pupil" class="pull-right">Add new pupils</v-btn>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel filter-panel">
                <div class="d-flex justify-space-between">
                    <div class="d-flex w-50 pb-2">
                        <v-select
                            v-model="selectedClass"
                            class="mr-5 mb-0"
                            density="compact"
                            hide-details
                            label="Class"
                            :items="classes"
                            clearable
                            :open-on-clear="false"
                            item-title="name"
                            item-value="uid"
                        ></v-select>
                    </div>
                    <div>
                        <v-btn variant="outlined" @click="printAction('login-details')" class="mr-2"
                            >Print login details</v-btn
                        >
                        <v-btn variant="outlined" @click="printAction('login-letters')">Print login letters</v-btn>
                    </div>
                </div>
            </div>
            <div class="panel">
                <v-data-table
                    v-model="selectedRows"
                    show-select
                    density="compact"
                    items-per-page="20"
                    :headers="headers"
                    :items="items"
                    item-value="id"
                    :sort-by="sortBy"
                    class="elevation-0"
                >
                    <template #[`item.last_active_details.timestamp`]="{ item }"
                        >{{ item.last_active_details.text }}
                    </template>
                    <template #[`item.action`]="{ item }">
                        <div class="d-flex">
                            <a class="mr-7" href="#" @click="printAction('login-letter', item)">
                                <v-icon icon="mdi-email-outline"></v-icon>
                                <v-tooltip activator="parent" location="top">Print login letter</v-tooltip>
                            </a>
                            <RouterLink class="mr-3" :to="`/admin/pupil/${item.id}`"
                                ><v-icon icon="mdi-square-edit-outline"></v-icon
                            ></RouterLink>
                            <a @click="deletePupil(item)"><v-icon color="error" icon="mdi-trash-can"></v-icon></a>
                        </div>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.pupils" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div id="js_print-login-details" class="d-none">
        <PrintLoginDetails :pupils="items" :school="school" />
    </div>
    <div id="js_print-login-letters" class="d-none">
        <PrintLoginLetters :pupils="items" :school="school" />
    </div>
    <div id="js_print-login-letter" class="d-none">
        <PrintLoginLetters v-if="pupilToPrint" :pupils="[pupilToPrint]" :school="school" />
    </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { DateTime } from 'luxon';
import { VDataTable } from 'vuetify/components/VDataTable';
import { PupilOverview } from '@ilteducation/giglets-types';
import PrintLoginDetails from '@/components/PrintLoginDetails.vue';
import print from '@/utils/print';

definePage({ meta: { area: 'pupils' } });

const store = useAdminStore();
store.fetchPupils();
store.fetchSchool();
const { pupils, school, isLoaded } = storeToRefs(store);

const confirm = useConfirmDialog();
const { formatDate } = useDateFormat();

const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const selectedRows = ref<Array<number>>([]);
const selectedClass = ref<string>();
const pupilToPrint = ref<PupilOverview>();

const items = computed(() => {
    return pupils.value
        .filter((p) => !(selectedClass.value && !p.classes.map((p) => p.uid).includes(selectedClass.value)))
        .map((pupil) => {
            let lastActiveText;
            let timestamp = 0;
            if (pupil.last_active) {
                const lastActive = DateTime.fromISO(pupil.last_active);
                if (lastActive.isValid) {
                    timestamp = lastActive.toMillis();
                    const daysAgo = lastActive.diffNow().negate().as('days');
                    if (daysAgo < 1) {
                        lastActiveText = 'Today';
                    } else if (daysAgo < 2) {
                        lastActiveText = 'Yesterday';
                    } else {
                        lastActiveText = formatDate(pupil.last_active);
                    }
                }
            }
            return {
                ...pupil,
                last_active_details: { timestamp, text: lastActiveText },
            };
        });
});

const unallocatedPupils = computed(() => items.value.filter((pupil) => !pupil.classes.length));

async function deletePupilRows() {
    const res = await confirm(
        'Delete pupil' + (selectedRows.value.length === 1 ? '' : 's'),
        selectedRows.value.length === 1
            ? `Are you sure you want to delete the selected pupil?`
            : `Are you sure you want to delete the ${selectedRows.value.length} selected pupils?`,
    );
    if (res) {
        await store.deletePupils(selectedRows.value);
        selectedRows.value = [];
        store.fetchPupils();
    }
}

async function deletePupil(item: any) {
    const res = await confirm('Delete pupil', `Are you sure you want to delete the pupil ${item.name}?`);
    if (res) {
        await store.deletePupils([item.id]);
        selectedRows.value = [];
        store.fetchPupils();
    }
}

const classes = computed(() => {
    const res: Array<{ uid: string; name: string }> = [];
    pupils.value.forEach((pupil) => {
        pupil.classes.forEach((c) => {
            if (!res.map((p) => p.uid).includes(c.uid)) {
                res.push(c);
            }
        });
    });
    return res.sort((a, b) => a.name.localeCompare(b.name));
});

const headers = [
    {
        title: 'Name',
        key: 'name',
    },
    { title: 'Username', key: 'username' },
    {
        title: 'Class(es)',
        key: 'classes',
        sortable: false,
        value: (item: any) =>
            (item as PupilOverview).classes
                .map((c) => c.name)
                .filter((value, index, array) => array.indexOf(value) === index)
                .join(', '),
    },
    {
        title: 'Last active',
        key: 'last_active_details.timestamp',
    },
    {
        title: '',
        key: 'action',
        sortable: false,
    },
];

const printAction = (type: string, pupil?: PupilOverview) => {
    pupilToPrint.value = pupil;
    nextTick(() => print(`js_print-${type}`));
};
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
.filter-panel {
    min-height: revert;
}
.v-table .v-icon {
    font-size: 1.1rem;
}
</style>
